import { formatMinorCurrency } from 'BoostXpressAdmin/Utils/cashReconciliationMoney'
import React, { useState } from 'react'
import Papa from 'papaparse'
import filterIcon from 'BoostXpressAdmin/Assets/Icon/Stations/filter.svg'
import exportIcon from 'BoostXpressAdmin/Assets/Icon/Stations/export.svg'
import { priceChangeBXService } from 'BoostXpressAdmin/Services/priceChangeBXService'
import { PriceChangeItem, PriceChangeProduct } from 'BoostXpressAdmin/Model/priceChangeXModel'
import { useDebouncedValue, useStationOperationsPage } from '../hooks/useStationOperationsList'
import { stationOperationsError } from 'BoostXpressAdmin/Utils/stationOperations'
import './PriceChange.scss'

const PriceChange: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('')
  const deferredStationId = useDebouncedValue(searchTerm.trim())
  const [productFilter, setProductFilter] = useState<PriceChangeProduct | 'all'>('all')
  const [isFilterOpen, setIsFilterOpen] = useState(false)
  const [page, setPage] = useState(1)
  const limit = 10

  const {
    items: items,
    total,
    isLoading,
    isError,
    error,
    refetch,
  } = useStationOperationsPage<
    PriceChangeItem,
    { stationId?: string; product?: PriceChangeProduct }
  >(
    'priceChange',
    priceChangeBXService.list,
    {
      stationId: deferredStationId || undefined,
      product: productFilter === 'all' ? undefined : productFilter,
    },
    page,
    limit,
  )
  const exportCsv = () => {
    if (!items.length) return
    const csv = Papa.unparse(
      items.map((item) => ({
        Station: item.station,
        Product: item.product,
        Pump: item.pump,
        'Totalizer Reading': item.totalizerReading.toLocaleString(),
        'New Price': formatMinorCurrency(item.newPriceMinor),
        'Report Date': item.effectiveFrom,
      })),
    )
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `price_changes_${new Date().toISOString().slice(0, 10)}.csv`
    document.body.appendChild(a)
    a.click()
    a.remove()
    window.URL.revokeObjectURL(url)
  }

  const formatPrice = formatMinorCurrency

  const totalPages = Math.ceil(total / limit) || 1

  const getPaginationItems = () => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1)
    }
    if (page <= 3) {
      return [1, 2, 3, '...', totalPages - 2, totalPages - 1, totalPages]
    }
    if (page >= totalPages - 2) {
      return [1, 2, 3, '...', totalPages - 2, totalPages - 1, totalPages]
    }
    return [1, '...', page - 1, page, page + 1, '...', totalPages]
  }

  return (
    <div className='bx-price-change-page'>
      {isError && (
        <div className='alert alert-danger' role='alert'>
          {stationOperationsError(error)}{' '}
          <button type='button' onClick={() => refetch()}>
            Try again
          </button>
        </div>
      )}
      {/* Header section matching Figma & Reconciliation */}
      <div className='bx-price-change-page__header'>
        <h2 className='bx-price-change-page__title'>Price Changes</h2>
        <p className='bx-price-change-page__subtitle'>
          Every price change is pinned to a totalizer reading, so engineers can tell exactly which
          sale used the old price and which used the new one.
        </p>
      </div>

      {/* Search & Actions Toolbar matching Reconciliation */}
      <div className='bx-price-change-page__controls'>
        <div className='bx-price-change-page__search'>
          <svg
            className='bx-price-change-page__search-icon'
            width='18'
            height='18'
            viewBox='0 0 24 24'
            fill='none'
            stroke='currentColor'
            strokeWidth='2'
            strokeLinecap='round'
            strokeLinejoin='round'
          >
            <circle cx='11' cy='11' r='8' />
            <line x1='21' y1='21' x2='16.65' y2='16.65' />
          </svg>
          <input
            type='search'
            aria-label='Filter price changes by station ID'
            placeholder='Filter by station ID'
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value)
              setPage(1)
            }}
          />
        </div>

        <div className='bx-price-change-page__actions'>
          <div className='bx-filter-dropdown-wrapper'>
            <button
              type='button'
              className={`bx-btn-outline ${productFilter !== 'all' ? 'active' : ''}`}
              onClick={() => setIsFilterOpen(!isFilterOpen)}
            >
              <img src={filterIcon} alt='' />
              <span>Filters</span>
            </button>

            {isFilterOpen && (
              <div className='bx-filter-menu'>
                <div className='bx-filter-menu__title'>Product</div>
                <button
                  type='button'
                  className={`bx-filter-menu__item ${productFilter === 'all' ? 'selected' : ''}`}
                  onClick={() => {
                    setProductFilter('all')
                    setPage(1)
                    setIsFilterOpen(false)
                  }}
                >
                  All Products
                </button>
                <button
                  type='button'
                  className={`bx-filter-menu__item ${productFilter === 'PMS' ? 'selected' : ''}`}
                  onClick={() => {
                    setProductFilter('PMS')
                    setPage(1)
                    setIsFilterOpen(false)
                  }}
                >
                  PMS
                </button>
                <button
                  type='button'
                  className={`bx-filter-menu__item ${productFilter === 'AGO' ? 'selected' : ''}`}
                  onClick={() => {
                    setProductFilter('AGO')
                    setPage(1)
                    setIsFilterOpen(false)
                  }}
                >
                  AGO
                </button>
              </div>
            )}
          </div>

          <button
            type='button'
            className='bx-btn-outline'
            onClick={exportCsv}
            disabled={isLoading || !items.length}
          >
            <img src={exportIcon} alt='' />
            <span>Export</span>
          </button>
        </div>
      </div>

      {/* Main Table matching Figma & Reconciliation */}
      <div className='bx-price-change-page__table-wrapper'>
        <table className='bx-price-change-table'>
          <thead>
            <tr>
              <th>Station</th>
              <th>Product</th>
              <th>Pump</th>
              <th>Totalizer reading</th>
              <th>New price</th>
              <th>Report date</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={6} className='bx-pc-empty'>
                  Loading price changes...
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={6} className='bx-pc-empty'>
                  No price changes found.
                </td>
              </tr>
            ) : (
              items.map((item) => (
                <tr key={item.id}>
                  <td className='bx-pc-col-station'>{item.station}</td>
                  <td className='bx-pc-col-product'>{item.product}</td>
                  <td className='bx-pc-col-pump'>{item.pump}</td>
                  <td className='bx-pc-col-reading'>{item.totalizerReading.toLocaleString()}</td>
                  <td className='bx-pc-col-price'>{formatPrice(item.newPriceMinor)}</td>
                  <td className='bx-pc-col-effective'>{item.effectiveFrom}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination matching Figma exactly */}
      <div className='bx-price-change-page__pagination'>
        <div className='bx-price-change-page__pagination-controls'>
          <button
            type='button'
            className='bx-pagination-nav'
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            ← Previous
          </button>

          <div className='bx-price-change-page__pagination-numbers'>
            {getPaginationItems().map((item, idx) =>
              item === '...' ? (
                <span key={`dots-${idx}`} className='bx-pagination-dots'>
                  ...
                </span>
              ) : (
                <button
                  key={`page-${item}`}
                  type='button'
                  className={`bx-pagination-num ${page === item ? 'active' : ''}`}
                  onClick={() => setPage(Number(item))}
                >
                  {item}
                </button>
              ),
            )}
          </div>

          <button
            type='button'
            className='bx-pagination-nav'
            disabled={page >= totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          >
            Next →
          </button>
        </div>
      </div>
    </div>
  )
}

export default PriceChange
